import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Center, Checkbox, Code, Divider, Flex,
  FormControl, FormHelperText, FormLabel, Heading, HStack, Input, Select, Skeleton, Stack, Text,
  useClipboard, useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import apiClient from '../auth/authenticatedClient';
import { errorToast } from '../error/errorToast';
import ProtectedHeader from '../ProtectedHeader';
import ProtectedFooter from '../ProtectedFooter';

const WRITE_SCOPE = 'entries:write';
const EXPIRY_OPTIONS = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
  { label: 'Never', days: null },
];
const DEFAULT_EXPIRY = '90 days';
const AGENT_API_URL = `${process.env.REACT_APP_API_URL}/agent/v1`;

const formatDay = (date) => format(new Date(date), 'd MMM yyyy');

/*
 * Personal access tokens that let an AI agent use the /agent/v1 API.
 * Tokens can read habits; check-ins are opt-in. A new token's secret is shown
 * once, right after it's created.
 */
export function ConnectedAgents() {
  const navigate = useNavigate();
  const toast = useToast();
  const [tokens, setTokens] = useState(null);
  const [newToken, setNewToken] = useState(null);

  useEffect(() => {
    apiClient.get('/api-tokens')
      .then((res) => setTokens(res.data))
      .catch(() => {
        setTokens([]);
        errorToast(toast, 'Could not load your agent tokens');
      });
  }, [toast]);

  const onCreated = (created) => {
    setNewToken(created);
    setTokens((current) => [created, ...current]);
  };

  const revoke = async (token) => {
    if (!window.confirm(`Revoke "${token.name}"? Any agent using it will lose access immediately.`)) return;
    try {
      await apiClient.delete(`/api-tokens/${token.id}`);
      setTokens((current) => current.filter((t) => t.id !== token.id));
      if (newToken?.id === token.id) setNewToken(null);
    } catch {
      errorToast(toast, 'Could not revoke the token');
    }
  };

  return (
    <Flex direction="column" h="100vh" minW="350px">
      <ProtectedHeader />
      <Center flex="1" alignItems="flex-start">
        <Box w={{ base: '100%', md: '85%', lg: '60%' }} p={{ base: '5', md: '8' }}>
          <Button variant="ghost" size="sm" leftIcon={<ChevronLeftIcon />} onClick={() => navigate('/habits')}>
            Habits
          </Button>
          <Heading size="md" mt="4">Connected agents</Heading>
          <Text mt="2" color="gray.600">
            Tokens let an AI agent check which habits you've done. They only work on the
            agent API at <Code>{AGENT_API_URL}</Code>, never on your account.
          </Text>

          <CreateTokenForm onCreated={onCreated} />
          {newToken && <NewTokenSecret token={newToken} />}

          <Divider my="6" />
          {tokens === null ? (
            <Stack><Skeleton height="20px" /><Skeleton height="20px" /></Stack>
          ) : tokens.length === 0 ? (
            <Text color="gray.600">No agents connected yet.</Text>
          ) : (
            <Stack divider={<Divider />} spacing="3">
              {tokens.map((token) => <TokenRow key={token.id} token={token} onRevoke={() => revoke(token)} />)}
            </Stack>
          )}
        </Box>
      </Center>
      <ProtectedFooter />
    </Flex>
  );
}

function CreateTokenForm({ onCreated }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [canCheckIn, setCanCheckIn] = useState(false);
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  const [isSaving, setIsSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      const res = await apiClient.post('/api-tokens', {
        name,
        scopes: canCheckIn ? [WRITE_SCOPE] : [],
        expiresInDays: EXPIRY_OPTIONS.find((o) => o.label === expiry).days,
      });
      onCreated(res.data);
      setName('');
      setCanCheckIn(false);
      setExpiry(DEFAULT_EXPIRY);
    } catch {
      errorToast(toast, 'Could not create the token');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack as="form" onSubmit={submit} spacing="4" mt="6">
      <FormControl isRequired>
        <FormLabel>Name</FormLabel>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude on my laptop" />
      </FormControl>
      <Checkbox isChecked={canCheckIn} onChange={(e) => setCanCheckIn(e.target.checked)}>
        Allow logging check-ins
      </Checkbox>
      <FormControl>
        <FormLabel>Expires after</FormLabel>
        <Select value={expiry} onChange={(e) => setExpiry(e.target.value)} maxW="xs">
          {EXPIRY_OPTIONS.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
        </Select>
        {expiry === 'Never' && (
          <FormHelperText color="orange.600">
            A token that never expires works until you revoke it. Revoke it here if it might have leaked.
          </FormHelperText>
        )}
      </FormControl>
      <Button type="submit" colorScheme="teal" alignSelf="flex-start" isLoading={isSaving}>
        Create token
      </Button>
    </Stack>
  );
}

function NewTokenSecret({ token }) {
  const { onCopy, hasCopied } = useClipboard(token.token);
  return (
    <Alert status="success" mt="4" flexDirection="column" alignItems="flex-start" gap="2" borderRadius="md">
      <HStack><AlertIcon /><Text fontWeight="bold">Copy this token now. You won't see it again.</Text></HStack>
      <AlertDescription w="100%">
        <HStack>
          <Code flex="1" p="2" wordBreak="break-all">{token.token}</Code>
          <Button size="sm" onClick={onCopy}>{hasCopied ? 'Copied' : 'Copy'}</Button>
        </HStack>
      </AlertDescription>
    </Alert>
  );
}

function TokenRow({ token, onRevoke }) {
  const canCheckIn = token.scopes.includes(WRITE_SCOPE);
  return (
    <Flex align="center" gap="3" wrap="wrap" data-testid={`token-${token.id}`}>
      <Box flex="1" minW="200px">
        <HStack>
          <Text fontWeight="semibold">{token.name}</Text>
          <Badge colorScheme={canCheckIn ? 'orange' : 'gray'}>{canCheckIn ? 'Read + check-ins' : 'Read only'}</Badge>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          <Code fontSize="xs">{token.prefix}…</Code>
          {' · '}{token.lastUsedAt ? `Last used ${formatDay(token.lastUsedAt)}` : 'Never used'}
          {' · '}{token.expiresAt ? `Expires ${formatDay(token.expiresAt)}` : 'Never expires'}
        </Text>
      </Box>
      <Button size="sm" variant="outline" colorScheme="red" onClick={onRevoke}>Revoke</Button>
    </Flex>
  );
}
